/**
 * Contract Intelligence settings: what a new review checks against unless
 * the person picks otherwise, and whether accepting a gap needs a reason.
 * Admins change them; members see them (the server checks the admin again).
 *
 * @module components/apps/contractIntelligence/CiSettingsPage
 */
import { useEffect, useRef, useState } from "react";

import { saveCiSettings, useCiSettings, type CiBasis, type CiSettings } from "../../../state/contractIntelligence";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { toastManager } from "../../ui/toast";
import { basisLine, BASIS_KIND_LABEL, refreshedDraft } from "./ciModel";
import { BasisPicker } from "./BasisPicker";
import { CiListSkeleton, CiPage, CiUnavailable } from "./ciParts";
import { useCiOrg } from "./useCiOrg";

function Row({ title, detail, children }: { readonly title: string; readonly detail: string; readonly children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 border-b border-border/50 py-5 last:border-b-0 sm:flex-row sm:items-start sm:gap-8">
      <div className="min-w-0 sm:w-64 sm:shrink-0">
        <h2 className="text-[13px] font-medium">{title}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function CiSettingsPage() {
  const { organizationId, canManage } = useCiOrg();
  const settings = useCiSettings(organizationId);
  const stored = settings.read?.kind === "ready" ? settings.read.value : null;
  const [draft, setDraft] = useState<CiSettings | null>(null);
  const [busy, setBusy] = useState(false);
  // A refresh (any write in the organization) must not discard unsaved edits.
  const lastLoaded = useRef<CiSettings | null>(null);
  useEffect(() => {
    if (!stored) return;
    const previous = lastLoaded.current;
    lastLoaded.current = stored;
    setDraft((draft) => refreshedDraft(draft, previous, stored));
  }, [stored]);
  const dirty = draft !== null && stored !== null && JSON.stringify(draft) !== JSON.stringify(stored);
  const save = async () => {
    if (!organizationId || !draft) return;
    setBusy(true);
    const outcome = await saveCiSettings(organizationId, draft);
    setBusy(false);
    toastManager.add(
      outcome.ok
        ? { type: "success", title: "Settings saved" }
        : { type: "error", title: "Settings not saved", description: outcome.message },
    );
  };
  const describe = (basis: CiBasis | null) => (basis ? `${BASIS_KIND_LABEL[basis.kind]}: ${basisLine(basis)}` : "None — people choose each time");

  return (
    <CiPage
      title="Settings"
      data-ci-page="settings"
      actions={
        canManage ? (
          <Button size="sm" disabled={!dirty || busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        ) : null
      }
    >
      {settings.read === null ? (
        <CiListSkeleton rows={3} />
      ) : settings.read.kind !== "ready" || !draft ? (
        <CiUnavailable what="Settings" onRetry={settings.reload} />
      ) : (
        <div className="mx-auto w-full max-w-3xl px-4 py-2 sm:px-6">
          {!canManage ? (
            <p className="mt-4 rounded-lg border border-border/70 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              Only organization admins can change these settings.
            </p>
          ) : null}
          <Row title="Default basis" detail="What a new review is checked against unless the person starting it chooses otherwise.">
            {canManage && organizationId ? (
              <div className="space-y-2">
                <BasisPicker
                  organizationId={organizationId}
                  value={draft.defaultBasis}
                  onChange={(defaultBasis) => setDraft({ ...draft, defaultBasis })}
                  compact
                />
                {draft.defaultBasis ? (
                  <Button size="xs" variant="ghost" onClick={() => setDraft({ ...draft, defaultBasis: null })}>
                    Clear the default
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="text-sm">{describe(draft.defaultBasis)}</p>
            )}
          </Row>
          <Row title="Reason to accept" detail="Ask for a short reason whenever someone accepts a gap instead of raising it.">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={draft.requireAcceptNote}
                disabled={!canManage}
                onCheckedChange={(checked) => setDraft({ ...draft, requireAcceptNote: checked })}
              />
              {draft.requireAcceptNote ? "A reason is required" : "A reason is optional"}
            </label>
          </Row>
          <Row title="Who can do what" detail="Set by membership in your organization.">
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li>Members start reviews, read results, accept gaps and ask Amoreg about them.</li>
              <li>Admins raise gaps as findings in Governance, and manage templates and these settings.</li>
              <li>Reviews are visible to everyone in the organization, and to no one outside it.</li>
            </ul>
          </Row>
        </div>
      )}
    </CiPage>
  );
}
